import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import { ICorrespondentResultViewModel, IFinancialReportViewModel } from 'viewModels';
import { IFilter, IOrderFieldInputModel } from 'props';
import { SolicitationController } from '../controller/SolicitationController';
import { useAuth } from './auth';
import { TypeUser } from '../types/enums';
import { UserController } from '../controller/UserController';

type PaymentContextProps = {
  children: ReactNode;
};
interface IInitialValues {
  payments: IFinancialReportViewModel[];
  paymentCorrespondents: ICorrespondentResultViewModel[];
  setPayments: React.Dispatch<React.SetStateAction<IFinancialReportViewModel[]>>;
  isLoadingPayments: boolean;
  isLoadingPaymentsByUser: boolean;
  isLoadingCorrespondents: boolean;
  isFetchingNextPagePayments: boolean;
  isFetchingNextPagePaymentsByUser: boolean;
  isFetchingNextPageCorrespondents: boolean;
  hasNextPagePayments: boolean | undefined;
  hasNextPagePaymentsByUser: boolean | undefined;
  hasNextPageCorrespondents: boolean | undefined;
  refetchPayments: () => void;
  refetchPaymentsByUser: () => void;
  fetchNextPagePayments: () => void;
  fetchNextPagePaymentsByUser: () => void;
  fetchNextPageCorrespondents: () => void;
  filterPayments: IFilter | undefined;
  setFilterPayments: React.Dispatch<React.SetStateAction<IFilter | undefined>>;
  setEnablePaymentsLoad: React.Dispatch<React.SetStateAction<boolean>>;
  paymentOrderBy: IOrderFieldInputModel<string>[];
  setPaymentOrderBy: React.Dispatch<React.SetStateAction<IOrderFieldInputModel<string>[]>>;
  inputFilterPayment?: {
    term?: string;
    filter?: string;
  };
}
const PaymentContext = createContext({} as IInitialValues);
const PAGE_SIZE = 20;
const PaymentContextProvider = ({ children }: PaymentContextProps) => {
  const { tokenIsValid, user } = useAuth();
  const [payments, setPayments] = useState<IFinancialReportViewModel[]>([]);
  const [filterPayments, setFilterPayments] = useState<IFilter>();
  const [enablePaymentsLoad, setEnablePaymentsLoad] = useState<boolean>(false);
  const [paymentCorrespondents, setPaymentCorrespondents] = useState<
    ICorrespondentResultViewModel[]
  >([]);
  const [paymentOrderBy, setPaymentOrderBy] = useState<IOrderFieldInputModel<string>[]>([]);
  const [inputFilterPayment] = useState<{
    term?: string;
    filter?: string;
  }>();

  const {
    isLoading: isLoadingCorrespondents,
    isFetchingNextPage: isFetchingNextPageCorrespondents,
    hasNextPage: hasNextPageCorrespondents,
    fetchNextPage: fetchNextPageCorrespondents,
  } = useInfiniteQuery(
    ['paymentCorrespondents'],
    ({ pageParam = 1 }) =>
      SolicitationController.getPaymentCorrespondents(user.id, pageParam, PAGE_SIZE),
    {
      onSuccess: (data) => {
        setPaymentCorrespondents((prev) => {
          const newCorrespondents = data.pages.flat(); // Junta todas as páginas
          const uniqueCorrespondents = Array.from(
            new Map([...prev, ...newCorrespondents].map((c) => [c.id, c])).values(), // Remove duplicados pelo id
          );
          return uniqueCorrespondents;
        });
      },
      enabled: tokenIsValid && !!user?.id && user.type !== TypeUser.USER,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
    },
  );

  const {
    isLoading: isLoadingPayments,
    isFetchingNextPage: isFetchingNextPagePayments,
    hasNextPage: hasNextPagePayments,
    refetch: refetchPayments,
    fetchNextPage: fetchNextPagePayments,
  } = useInfiniteQuery(
    [
      'payments',
      paymentOrderBy,
      inputFilterPayment?.term,
      inputFilterPayment?.filter,
      filterPayments,
    ],
    ({ pageParam = 1 }) =>
      SolicitationController.getFinancialReport({
        orderBy: paymentOrderBy,
        ...inputFilterPayment,
        end: filterPayments?.end,
        page: pageParam,
        take: PAGE_SIZE,
        start: filterPayments?.start,
        clientIds: filterPayments?.clientIds,
        departamentIds: filterPayments?.departamentIds,
        statusList: filterPayments?.statusList,
        typeAudienceList: filterPayments?.typeAudienceList,
        foldersNumbers: filterPayments?.foldersNumbers,
        partsNames: filterPayments?.partsNames,
        specialtiesIds: filterPayments?.specialtiesIds,
        correspondencesIds: filterPayments?.correspondencesIds,
        filterDateType: filterPayments?.filterDateType,
        distance: filterPayments?.distance,
        correspondentsIds: filterPayments?.correspondentsIds,
        protocols: filterPayments?.protocols,
        paymentStatusList: filterPayments?.paymentStatusList,
      }),
    {
      onSuccess: (data) => {
        const newPayments = data.pages.flatMap((page) => page);
        setPayments(newPayments);
      },
      refetchOnWindowFocus: false,
      enabled: tokenIsValid && !!user?.id && user.type !== TypeUser.USER && enablePaymentsLoad,
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
    },
  );

  const {
    isLoading: isLoadingPaymentsByUser,
    isFetchingNextPage: isFetchingNextPagePaymentsByUser,
    hasNextPage: hasNextPagePaymentsByUser,
    refetch: refetchPaymentsByUser,
    fetchNextPage: fetchNextPagePaymentsByUser,
  } = useInfiniteQuery(
    [
      'payments',
      paymentOrderBy,
      inputFilterPayment?.term,
      inputFilterPayment?.filter,
      filterPayments,
    ],
    ({ pageParam = 1 }) =>
      UserController.getFinancialReportByUser({
        orderBy: paymentOrderBy,
        ...inputFilterPayment,
        page: pageParam,
        take: PAGE_SIZE,
      }),
    {
      onSuccess: (data) => {
        const newPayments = data.pages.flatMap((page) => page);
        setPayments(newPayments);
      },
      refetchOnWindowFocus: false,
      enabled: tokenIsValid && !!user?.id && user.type === TypeUser.USER,
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
    },
  );

  useEffect(() => {
    if (!tokenIsValid) {
      setPayments([]);
      setFilterPayments(undefined);
      setPaymentCorrespondents([]);
      setPaymentOrderBy([]);
    }
  }, [tokenIsValid]);

  const contextValues = useMemo(
    () => ({
      isLoadingPayments,
      isLoadingPaymentsByUser,
      isLoadingCorrespondents,
      isFetchingNextPagePayments,
      isFetchingNextPagePaymentsByUser,
      isFetchingNextPageCorrespondents,
      hasNextPagePayments,
      hasNextPagePaymentsByUser,
      hasNextPageCorrespondents,
      refetchPayments,
      refetchPaymentsByUser,
      fetchNextPagePayments,
      fetchNextPagePaymentsByUser,
      fetchNextPageCorrespondents,
      filterPayments,
      setFilterPayments,
      setEnablePaymentsLoad,
      payments,
      setPayments,
      paymentOrderBy,
      setPaymentOrderBy,
      inputFilterPayment,
      paymentCorrespondents,
    }),
    [
      isLoadingPayments,
      isLoadingPaymentsByUser,
      isLoadingCorrespondents,
      isFetchingNextPagePayments,
      isFetchingNextPagePaymentsByUser,
      isFetchingNextPageCorrespondents,
      hasNextPagePayments,
      hasNextPagePaymentsByUser,
      hasNextPageCorrespondents,
      refetchPayments,
      refetchPaymentsByUser,
      fetchNextPagePayments,
      fetchNextPagePaymentsByUser,
      fetchNextPageCorrespondents,
      setEnablePaymentsLoad,
      filterPayments,
      payments,
      paymentOrderBy,
      inputFilterPayment,
      paymentCorrespondents,
    ],
  );
  return <PaymentContext.Provider value={contextValues}>{children}</PaymentContext.Provider>;
};
export const usePayment = () => useContext(PaymentContext);
export default PaymentContextProvider;
